<script setup lang="ts">
  import CompleteTime from '@/components/CompleteTime.vue';
  import ModalPosNewOrder from '@/components/modals/pos/new-order/ModalPosNewOrder.vue';
  import ModalPosViewOrder from '@/components/modals/pos/view-order/ModalPosViewOrder.vue';
  import { useModal } from '@/composition/use-modal';
  import { useOrders } from '@/composition/use-orders';
  import {
    ORDER_PAYMENT_STATUSES_MAP,
    ORDER_STATUSES_MAP,
    ORDER_TYPES_MAP,
  } from '@/const/orders.const';
  import { OrderPaymentStatus, OrderType } from '@/enums/OrderEnums';
  import { IOrder } from '@/interfaces/IOrder';
  import { useOrdersStore } from '@/stores/orders.store';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { displayPrice } from '@/utils';
  import { orderItemsToText } from '@/utils/orders.utils';
  import { computed } from 'vue';

  const props = withDefaults(
    defineProps<{
      order: IOrder;
      hideMainInfo?: boolean;
      selected?: boolean;
      isPosCall?: boolean;
    }>(),
    {
      isPosCall: true,
    }
  );

  const restaurantStore = useRestaurantStore();
  const ordersStore = useOrdersStore();
  const orderHelper = useOrders();

  const orderType = computed(() => ORDER_TYPES_MAP.get(props.order.type));

  const orderStatus = computed(() =>
    ORDER_STATUSES_MAP.get(props.order.status)
  );

  const paymentStatus = computed(() => {
    let statusId = props.order.paymentStatus;

    if (
      props.order.paymentStatus === OrderPaymentStatus.Awaiting &&
      props.order.totalPaid > 0
    ) {
      statusId = OrderPaymentStatus.Partial;
    }

    return ORDER_PAYMENT_STATUSES_MAP.get(statusId);
  });

  const mainInfo = computed(() => {
    switch (props.order.type) {
      case OrderType.Seat:
        return {
          icon: 'mdi-table-furniture',
          text: `${props.order.table?.name}`,
        };
      case OrderType.Delivery:
        return {
          icon: 'mdi-map-marker',
          text: props.order.userAddress,
        };
      case OrderType.Away:
        return {
          icon: 'mdi-receipt',
          text: `#${props.order.humanId}`,
        };
    }
  });

  const itemsText = computed(() =>
    orderItemsToText({ order: props.order, maxItems: 3 })
  );

  const posInfo = computed(() => {
    const o = props.order;

    if (!o.manual) {
      return {
        icon: 'mdi-cellphone',
        text: 'App',
        color: 'blue',
        dark: true,
      };
    }

    if (o.deviceId === restaurantStore.deviceId) {
      return {
        icon: 'mdi-cash-register',
        text: 'Este POS',
        color: 'green',
        dark: true,
      };
    }

    return {
      icon: 'mdi-cash-register',
      text: 'POS',
      color: 'grey',
      dark: true,
    };
  });

  function openOrder() {
    orderHelper.openOrder(props.order, props.isPosCall ?? true);
  }

  function editOrder() {
    orderHelper.editOrder(props.order);
  }
</script>

<template>
  <div
    class="pos-order"
    :class="{
      [`status--${orderStatus.id}`]: true,
      selected,
    }"
    @click="openOrder"
  >
    <div class="header">
      <div class="header-main">
        <div class="header-main-row">
          <v-icon small class="type-icon">{{ orderType.icon }}</v-icon>
          <template v-if="!hideMainInfo">
            <div class="main-info info-line">
              <v-icon small class="tw-pr-0.5">{{ mainInfo.icon }}</v-icon>
              <span class="tw-truncate">{{ mainInfo.text }}</span>
            </div>
            <div class="item-separator">•</div>
          </template>
          <div
            v-if="order.userName"
            class="username info-line"
            :class="{
              ['is-main']: hideMainInfo,
            }"
          >
            <v-icon small>mdi-account</v-icon>
            <span class="tw-truncate">{{ order.userName }}</span>
          </div>
        </div>
      </div>
      <div class="total-container">
        <span class="order-total">
          {{ restaurantStore.currency?.symbol }} {{ displayPrice(order.total) }}
        </span>

        <v-chip
          class="payment-status"
          :color="paymentStatus.color"
          x-small
          :dark="paymentStatus.chipTheme === 'dark'"
        >
          <v-icon class="pr-1" small>mdi-cash</v-icon>
          {{ paymentStatus.title }}
        </v-chip>
      </div>
    </div>
    <div class="body">
      <div class="body--info">
        <div v-if="itemsText" class="info-line items">
          <v-icon small>mdi-cart</v-icon>
          <span class="items-text">{{ itemsText }}</span>
        </div>
        <div class="tw-flex tw-space-x-1 tw-items-centers">
          <CompleteTime :date="order.createdAt" hide-ago class="time" />
          <v-chip
            class="order-status"
            :color="orderStatus.color"
            x-small
            :dark="orderStatus.chipTheme === 'dark'"
          >
            <v-icon class="pr-1" x-small>{{ orderStatus.icon }}</v-icon>
            {{ orderStatus.title }}
          </v-chip>
          <v-chip
            class="pos-info"
            :color="posInfo.color"
            x-small
            :dark="posInfo.dark"
          >
            <v-icon class="pr-1" x-small>{{ posInfo.icon }}</v-icon>
            {{ posInfo.text }}
          </v-chip>
        </div>
      </div>
      <div class="body--actions">
        <v-btn class="action-btn view" color="primary" x-small>
          <v-icon x-small class="tw-pr-0.5">mdi-eye</v-icon>
          Ver
        </v-btn>
        <v-btn
          v-if="isPosCall"
          class="action-btn edit"
          color="info"
          x-small
          @click.stop="editOrder"
        >
          <v-icon x-small class="tw-pr-0.5">mdi-pencil</v-icon>
          Editar
        </v-btn>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .pos-order {
    @apply tw-border tw-bg-white tw-border-gray-300 tw-rounded-md tw-overflow-hidden tw-text-sm tw-cursor-pointer;
    @apply tw-divide-y tw-divide-gray-200 tw-divide-dashed;

    .info-line {
      @apply tw-flex tw-items-center tw-space-x-0.5;
    }

    .header {
      @apply tw-flex tw-py-1 tw-px-2 tw-flex-nowrap;
      @apply tw-flex-col tw-items-stretch tw-space-y-1 sm:tw-space-y-0 sm:tw-flex-row sm:tw-items-center sm:tw-space-x-1;
      @apply tw-text-gray-600;

      .item-separator {
        @apply tw-text-gray-400;
      }

      .header-main {
        @apply tw-flex tw-flex-col tw-flex-1 tw-space-y-1 tw-min-w-1;

        .header-main-row {
          @apply tw-flex tw-items-center tw-space-x-1;

          .type-icon {
            @apply tw-text-gray-400;
          }
        }
      }

      .main-info {
        @apply tw-font-medium tw-truncate tw-min-w-1;
      }

      .username {
        @apply tw-font-normal tw-truncate tw-min-w-1;

        &.is-main {
          @apply tw-font-medium;
        }
      }

      .total-container {
        @apply tw-flex tw-space-x-1.5 tw-items-center tw-justify-end sm:tw-justify-normal;

        .order-total {
          @apply tw-font-semibold;
        }
      }
    }

    .body {
      @apply tw-py-1 tw-px-2 tw-flex tw-items-stretch tw-space-x-2;

      &--info {
        @apply tw-space-y-1.5 tw-flex tw-flex-col tw-flex-1 tw-min-w-1;
        @apply md:tw-space-y-0 md:tw-space-x-1.5 md:tw-flex-row md:tw-items-center md:tw-space-x-reverse;

        .items {
          @apply tw-text-xs md:tw-order-1 tw-truncate tw-flex-1 tw-min-w-1 tw-flex tw-items-center tw-space-x-1 tw-font-light;

          .items-text {
            @apply tw-truncate tw-flex-1 tw-min-w-1;
          }
        }

        .time {
        }
      }

      &--actions {
        @apply tw-flex tw-items-center tw-space-x-1;

        .action-btn {
          @apply tw-shadow-none tw-rounded-lg tw-px-1 tw-min-w-1;
        }
      }

      .order-status,
      .pos-info {
        @apply tw-px-1.5;
      }
    }

    .footer {
      @apply tw-flex tw-items-center tw-justify-between tw-space-x-1 tw-py-1 tw-px-2;
    }

    &:hover {
      @apply tw-bg-gray-50;
    }

    &.selected {
      @apply tw-bg-orange-50;
    }

    &.status--progress {
    }
  }
</style>
